import React, { useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { isIOS } from '../lib/platform';
import {
  Apple, Smartphone, Share, PlusSquare, Home, BellRing,
  MoreVertical, Download, Check, Copy,
} from 'lucide-react';
import { toast } from 'sonner';

const STEP_ICONS = { share: Share, plus: PlusSquare, home: Home, bell: BellRing, menu: MoreVertical, download: Download };

const IOS_STEPS = [
  { icon: 'share', ar: 'افتح tababa.co في متصفح Safari واضغط على زر المشاركة في أسفل الشاشة.', en: 'Open tababa.co in Safari and tap the Share button at the bottom of the screen.' },
  { icon: 'plus', ar: 'اختر «إضافة إلى الشاشة الرئيسية» من القائمة، ثم اضغط «إضافة».', en: 'Choose "Add to Home Screen" from the list, then tap "Add".' },
  { icon: 'home', ar: 'أغلق Safari وافتح تطبيق Tababa.co من الشاشة الرئيسية.', en: 'Close Safari and open the Tababa.co app from your home screen.' },
  { icon: 'bell', ar: 'اضغط «تفعيل الآن» عند ظهور رسالة الإشعارات، ثم «السماح». ستصلك الإشعارات حتى والهاتف مقفل.', en: 'Tap "Enable now" when the notifications message appears, then "Allow". Alerts will arrive even when your phone is locked.' },
];

const ANDROID_STEPS = [
  { icon: 'download', ar: 'افتح tababa.co في متصفح Chrome، وستظهر رسالة «تثبيت التطبيق» في الأسفل — اضغط عليها.', en: 'Open tababa.co in Chrome — an "Install app" banner appears at the bottom. Tap it.' },
  { icon: 'menu', ar: 'إذا لم تظهر الرسالة: اضغط على قائمة النقاط الثلاث ⋮ ثم «تثبيت التطبيق» أو «إضافة إلى الشاشة الرئيسية».', en: 'If you don\'t see it: tap the three-dot menu ⋮ then "Install app" / "Add to Home screen".' },
  { icon: 'home', ar: 'افتح Tababa.co من الشاشة الرئيسية مثل أي تطبيق عادي.', en: 'Open Tababa.co from your home screen like any normal app.' },
  { icon: 'bell', ar: 'اضغط «السماح» عند طلب الإشعارات لتصلك تنبيهات المكالمات والمواعيد فوراً.', en: 'Tap "Allow" when notifications are requested so call and appointment alerts arrive instantly.' },
];

const StepList = ({ steps, accent }) => {
  const { t } = useLanguage();
  return (
    <ol className="space-y-3">
      {steps.map((s, i) => {
        const Icon = STEP_ICONS[s.icon];
        return (
          <li key={s.en} className="flex items-start gap-3" data-testid={`install-step-${i + 1}`}>
            <span className={`w-10 h-10 rounded-2xl ${accent} text-white flex items-center justify-center flex-shrink-0 font-bold`}>
              {i + 1}
            </span>
            <div className="flex-1 pt-1.5">
              <p className="text-sm text-gray-800 leading-relaxed">{t(s.ar, s.en)}</p>
            </div>
            <Icon size={20} className="text-gray-400 mt-3 flex-shrink-0" />
          </li>
        );
      })}
    </ol>
  );
};

export default function InstallGuidePage() {
  const { t, language } = useLanguage();
  const [tab, setTab] = useState(isIOS() ? 'ios' : 'android');

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText('https://tababa.co/install');
      toast.success(t('تم نسخ الرابط', 'Link copied'));
    } catch {
      toast.error(t('تعذّر النسخ', 'Could not copy'));
    }
  };

  return (
    <div className={`min-h-screen bg-gradient-to-b from-[#e3f2fd] to-white py-10 ${language === 'ar' ? 'rtl' : 'ltr'}`}
         dir={language === 'ar' ? 'rtl' : 'ltr'}>
      <div className="container mx-auto px-4 max-w-2xl">
        <div className="text-center mb-8">
          <img src="/icons/icon-192.png" alt="Tababa.co" className="w-20 h-20 rounded-2xl mx-auto shadow-lg mb-4" />
          <h1 className="text-3xl sm:text-4xl font-extrabold text-[#0d47a1] mb-2" data-testid="install-guide-title">
            {t('ثبّت تطبيق Tababa.co', 'Install the Tababa.co app')}
          </h1>
          <p className="text-base text-gray-600">
            {t('خطوتان فقط لتصلك إشعارات المكالمات والمواعيد حتى والتطبيق مغلق.',
               'A couple of steps so call and appointment alerts reach you even when the app is closed.')}
          </p>
        </div>

        <div className="flex gap-2 mb-6 bg-white rounded-2xl p-1.5 shadow-sm">
          <button
            onClick={() => setTab('ios')}
            className={`flex-1 flex items-center justify-center gap-2 rounded-xl py-2.5 text-sm font-bold transition-colors ${tab === 'ios' ? 'bg-[#0d47a1] text-white' : 'text-gray-600 hover:bg-gray-50'}`}
            data-testid="install-tab-ios"
          >
            <Apple size={16} /> iPhone / iPad
          </button>
          <button
            onClick={() => setTab('android')}
            className={`flex-1 flex items-center justify-center gap-2 rounded-xl py-2.5 text-sm font-bold transition-colors ${tab === 'android' ? 'bg-emerald-600 text-white' : 'text-gray-600 hover:bg-gray-50'}`}
            data-testid="install-tab-android"
          >
            <Smartphone size={16} /> Android
          </button>
        </div>

        <Card className="p-6" data-testid="install-guide-steps">
          {tab === 'ios' ? (
            <>
              <StepList steps={IOS_STEPS} accent="bg-[#0d47a1]" />
              <p className="mt-5 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-xl p-3 leading-relaxed">
                {t(
                  'مهم على iPhone: لا تصل الإشعارات من متصفح Safari مباشرة — يجب فتح التطبيق من الشاشة الرئيسية (يتطلب iOS 16.4 أو أحدث).',
                  'Important on iPhone: notifications never arrive from a Safari tab — you must open the app from your home screen (requires iOS 16.4 or newer).',
                )}
              </p>
            </>
          ) : (
            <StepList steps={ANDROID_STEPS} accent="bg-emerald-600" />
          )}
        </Card>

        <Card className="p-5 mt-5">
          <h2 className="text-base md:text-lg font-bold text-[#2c3e50] mb-3 flex items-center gap-2">
            <Check size={18} className="text-green-600" />
            {t('بعد التثبيت ستحصل على', 'After installing you get')}
          </h2>
          <ul className="space-y-2 text-sm text-gray-700">
            {[
              ['إشعارات المكالمات الواردة ورنين على الشاشة الكاملة', 'Incoming call alerts with a full-screen ring'],
              ['تنبيهات المواعيد قبل موعدها', 'Appointment reminders before your time'],
              ['فتح أسرع من الشاشة الرئيسية بدون متصفح', 'Faster launch from your home screen, no browser bar'],
            ].map(([ar, en]) => (
              <li key={en} className="flex items-start gap-2">
                <Check size={16} className="text-green-500 mt-0.5 flex-shrink-0" />
                <span>{t(ar, en)}</span>
              </li>
            ))}
          </ul>
        </Card>

        <div className="mt-6 text-center">
          <Button onClick={copyLink} variant="outline" className="rounded-full" data-testid="install-copy-link-btn">
            <Copy size={15} className="me-2" />
            {t('انسخ رابط هذه الصفحة لإرسالها', 'Copy this page link to share')}
          </Button>
          <p className="text-xs text-gray-400 mt-2">tababa.co/install</p>
        </div>
      </div>
    </div>
  );
}
